import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState<'hp' | 'lp'>('lp');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // スクロール検知
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "つくーるうぇぶ",
          "description": "Web制作が苦手な方へ。ホームページもランディングページもまるっとおまかせ。チャット1つで更新OK。",
          "inLanguage": "ja"
        },
        {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          "name": "つくーるうぇぶ",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/ee58861ba7b6c31a9af012601f80f1d3/1349779ff3cc17dc8b22c137c4c0afcc.jpeg"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "availableLanguage": "Japanese"
          }
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": siteUrl,
          "name": "つくーるうぇぶ｜HPもLPもチャット1つで更新OK｜Web制作サービス",
          "isPartOf": {
            "@id": `${siteUrl}/#website`
          },
          "about": {
            "@id": `${siteUrl}/#organization`
          },
          "description": "Web制作が苦手な方へ。ホームページもランディングページもまるっとおまかせ。チャット1つで更新OK。月額4,980円から。専門知識不要で事業に専念できます。",
          "inLanguage": "ja"
        },
        {
          "@type": "Service",
          "serviceType": "Web制作サービス",
          "provider": {
            "@id": `${siteUrl}/#organization`
          },
          "areaServed": "JP",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Web制作サービス",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "ホームページ制作",
                  "description": "オリジナルデザインのホームページ制作。スマホ対応、SEO対策標準装備。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "ランディングページ制作",
                  "description": "コンバージョン重視のLP制作。心理学に基づいた構成で成果を最大化。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "チャット更新サポート",
                  "description": "チャット1つで簡単更新。専門知識不要で最短30分対応。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "運用サポート",
                  "description": "公開後も継続サポート。アクセス解析、改善提案、トラブル対応まで。"
                }
              }
            ]
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Web知識がなくても大丈夫ですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "もちろん大丈夫です! つくーるうぇぶは、Web知識ゼロの方でも安心してご利用いただけるよう、専門用語を使わず、やさしい言葉で丁寧にご説明します。わからないことがあれば、何度でもお気軽にご質問ください。"
              }
            },
            {
              "@type": "Question",
              "name": "更新はどのくらいの頻度でお願いできますか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "更新回数に制限はありません。チャットで「この写真を変えたい」「新しいメニューを追加したい」と送っていただくだけで、何度でも対応いたします。小さな修正から大きな変更まで、お気軽にご依頼ください。"
              }
            },
            {
              "@type": "Question",
              "name": "料金プランを教えてください",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "初期制作費用と月額4,980円（サーバー・ドメイン・保守込み）のプランをご用意しています。更新作業は別途対応となりますが、チャットで気軽にご依頼いただけます。詳しい料金は無料相談時にご案内いたします。"
              }
            },
            {
              "@type": "Question",
              "name": "制作期間はどのくらいですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "ホームページもLPも1週間以内で公開可能です。お急ぎの場合は特急対応も承りますので、ご相談ください。"
              }
            },
            {
              "@type": "Question",
              "name": "チャットでの更新対応の範囲は？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "テキストや画像の差し替え、新規ページの追加、デザインの微調整など、幅広く対応しています。「こんなこと頼んでいいのかな？」と思うことでも、まずはお気軽にご相談ください。"
              }
            },
            {
              "@type": "Question",
              "name": "スマホ対応はしていますか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "はい、すべてのサイトがスマホ・タブレット・PCに完全対応しています。どのデバイスでも美しく、使いやすいデザインをお届けします。"
              }
            },
            {
              "@type": "Question",
              "name": "公開後のサポートはありますか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "公開後も継続的にサポートいたします。アクセス解析、改善提案、トラブル対応など、長期的にお付き合いさせていただきます。困ったときはいつでもご連絡ください。"
              }
            },
            {
              "@type": "Question",
              "name": "他社で作ったサイトのリニューアルも可能ですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "もちろん可能です! 既存サイトの問題点を分析し、より良いサイトへとリニューアルいたします。まずは現状のサイトを拝見させてください。"
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'schema-org-jsonld';
    
    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('schema-org-jsonld');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // ニュースレター登録処理
    alert('ご登録ありがとうございます! 💌');
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-[#FFFEF9]">
      {/* ナビゲーション */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src="https://static.readdy.ai/image/ee58861ba7b6c31a9af012601f80f1d3/1349779ff3cc17dc8b22c137c4c0afcc.jpeg" 
              alt="つくーるうぇぶ" 
              className="h-10 sm:h-12 w-auto object-contain"
            />
            <span className="text-xl sm:text-2xl font-bold text-gray-800">つくーるうぇぶ</span>
          </div>
          
          {/* デスクトップメニュー */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a href="#service" className="text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer text-sm xl:text-base">サービス</a>
            <a href="#voice" className="text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer text-sm xl:text-base">お客様の声</a>
            <a href="#pricing" className="text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer text-sm xl:text-base">料金プラン</a>
            <a href="#comparison" className="text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer text-sm xl:text-base">比較</a>
            <a href="#faq" className="text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer text-sm xl:text-base">FAQ</a>
            <a 
              href="#contact" 
              className="bg-[#FF9B9B] text-white px-4 xl:px-6 py-2 xl:py-2.5 rounded-full hover:bg-[#FF6B6B] transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base"
            >
              無料相談 💬
            </a>
          </div>

          {/* モバイルメニューボタン */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
            aria-label="メニュー"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* モバイルメニュー */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a 
                href="#service" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
              >
                サービス
              </a>
              <a 
                href="#voice" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
              >
                お客様の声
              </a>
              <a 
                href="#pricing" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
              >
                料金プラン
              </a>
              <a 
                href="#comparison" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
              >
                比較
              </a>
              <a 
                href="#faq" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:text-[#FF9B9B] transition-colors cursor-pointer"
              >
                FAQ
              </a>
              <a 
                href="#contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#FF9B9B] text-white px-6 py-3 rounded-full hover:bg-[#FF6B6B] transition-all text-center whitespace-nowrap cursor-pointer"
              >
                無料相談 💬
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ヒーローセクション */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* 背景グラデーション */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFE5E5]/30 via-[#F5F0FF]/20 to-[#E8F5F1]/30"></div>
        <div className="absolute top-20 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-[#FFD3B6]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 sm:w-80 h-64 sm:h-80 bg-[#E0BBE4]/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid md:grid-cols-[70%_30%] gap-8 sm:gap-12 items-center">
          {/* 左側コンテンツ */}
          <div className="space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 bg-[#FFE5E5] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <span className="text-xs sm:text-sm text-gray-700">✨ Web制作が苦手な方へ</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              <span className="block text-gray-800">HPもLPも</span>
              <span className="block text-[#E0BBE4]">まるっとおまかせ</span>
              <span className="block text-gray-800">チャット1つで更新OK</span>
            </h1>

            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl">
              事業に専念したいあなたのために、Webのすべてをサポートします。<br className="hidden sm:block" />
              難しいことは全部私たちにおまかせ。あなたは本業に集中できます 🌸
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <a 
                href="#contact"
                className="bg-[#FF9B9B] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#FF6B6B] transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-center"
              >
                無料相談してみる 💬
              </a>
              <a 
                href="#service"
                className="bg-transparent border border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:border-gray-400 transition-all whitespace-nowrap cursor-pointer text-center"
              >
                サービスを見る →
              </a>
            </div>
          </div>

          {/* 右側ビジュアル */}
          <div className="relative hidden md:block">
            <div className="relative bg-white/80 backdrop-blur-sm rounded-[32px] p-6 sm:p-8 shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/7461e03b7ad5ec41df15ff8fb87ecf40.jpg"
                alt="Web制作イメージ"
                className="w-full h-full object-cover rounded-2xl"
              />
              {/* 装飾要素 */}
              <div className="absolute -top-4 -right-4 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl sm:text-2xl animate-bounce">
                ⭐
              </div>
              <div className="absolute -bottom-4 -left-4 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl sm:text-2xl animate-bounce" style={{ animationDelay: '0.5s' }}>
                💖
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サービス紹介セクション */}
      <section id="service" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* タイトル */}
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-[#FFE5E5] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <span className="text-xs sm:text-sm text-gray-700">🌸 つくーるうぇぶの特徴</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              <span className="block text-gray-800">Webの悩み、</span>
              <span className="block text-[#B4E7CE]">ぜんぶ解決します</span>
            </h2>
          </div>

          {/* カードグリッド */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {/* カード1 */}
            <div className="bg-[#FFF0F0] rounded-3xl p-6 sm:p-8 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=cute%20illustration%20of%20laptop%20computer%20with%20heart%20icon%2C%20pastel%20pink%20background%2C%20friendly%20web%20design%20concept%2C%20simple%20minimalist%20style%2C%20warm%20and%20welcoming%20atmosphere&width=300&height=300&seq=service-1&orientation=squarish"
                  alt="制作おまかせ"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center leading-tight">
                HPもLPも<br />おまかせ制作
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                デザインから公開まで、すべて私たちが担当。あなたは事業に集中できます✨
              </p>
            </div>

            {/* カード2 */}
            <div className="bg-[#F5F0FF] rounded-3xl p-6 sm:p-8 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=cute%20illustration%20of%20smartphone%20with%20chat%20bubbles%20and%20messaging%20icons%2C%20pastel%20lavender%20background%2C%20friendly%20communication%20concept%2C%20simple%20minimalist%20style%2C%20warm%20and%20approachable%20design&width=300&height=300&seq=service-2&orientation=squarish"
                  alt="チャット更新"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center leading-tight">
                更新は<br />チャット1つでOK
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                『ここ直して』とチャットで送るだけ。専門知識ゼロでも大丈夫です💬
              </p>
            </div>

            {/* カード3 */}
            <div className="bg-[#F0FFF4] rounded-3xl p-6 sm:p-8 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer sm:col-span-2 md:col-span-1">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=cute%20illustration%20of%20shield%20with%20checkmark%20icon%2C%20pastel%20mint%20green%20background%2C%20security%20and%20support%20concept%2C%20simple%20minimalist%20style%2C%20trustworthy%20and%20reliable%20design&width=300&height=300&seq=service-3&orientation=squarish"
                  alt="運用サポート"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center leading-tight">
                安心の<br />運用サポート
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                公開後も継続サポート。困ったときはいつでも相談できます🌱
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ターゲットの悩みと共感セクション */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#FFF5F5] to-[#F8F8FF]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm">
              <span className="text-xs sm:text-sm text-gray-700">😢 こんなお悩み、ありませんか？</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              Webのこと、<br className="md:hidden" />よくわからない...
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {[
              { icon: '💻', text: 'ホームページを作りたいけど、何から始めればいいかわからない' },
              { icon: '😰', text: '制作会社に頼んだら、専門用語ばかりで理解できなかった' },
              { icon: '⏰', text: '更新したいけど、業者に頼むと時間もお金もかかる' },
              { icon: '📱', text: 'チャットやSNSで気軽に相談できる相手がいない' },
              { icon: '💸', text: '予算が限られているから、コスパよく作りたい' },
              { icon: '🤔', text: '公開後のサポートがなくて、困ったときに誰にも聞けない' },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-4 sm:p-6 shadow-md hover:shadow-lg transition-all">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0">
                    {item.icon}
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pt-2">{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-[#FF9B9B] to-[#FFB6B6] rounded-3xl p-6 sm:p-10 text-center text-white shadow-xl">
            <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
              そんなあなたに、<br className="md:hidden" />つくーるうぇぶがあります 🌸
            </p>
            <p className="text-base sm:text-lg opacity-90">
              難しいことは全部おまかせ。あなたは本業に集中するだけでOKです!
            </p>
          </div>
        </div>
      </section>

      {/* サービス詳細セクション */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-[#F0FFF4] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <span className="text-xs sm:text-sm text-gray-700">📋 サービス内容</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              つくーるうぇぶで<br className="md:hidden" />できること
            </h2>
          </div>

          <div className="space-y-12 sm:space-y-16 lg:space-y-12">
            {/* サービス1 */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-[#FFF0F0] inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm font-semibold text-[#FF6B6B]">01. ホームページ制作</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
                  あなたの事業を<br />魅力的に伝えるHP
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  お客様の事業内容やターゲットに合わせて、オリジナルのホームページを制作します。デザインから文章作成、公開まで全部おまかせ。あなたは事業の魅力を教えてくれるだけでOKです ✨
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {['デザイン完全オリジナル', 'スマホ対応もバッチリ', 'SEO対策も標準装備', '公開前の修正は何度でも無料'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#FF9B9B] rounded-full text-white text-xs sm:text-sm flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=beautiful%20modern%20website%20homepage%20design%20on%20desktop%20computer%20screen%2C%20pastel%20color%20scheme%20with%20pink%20and%20mint%20accents%2C%20clean%20professional%20layout%2C%20business%20website%20mockup%2C%20soft%20lighting%2C%20minimalist%20aesthetic&width=600&height=450&seq=service-detail-1&orientation=landscape"
                    alt="ホームページ制作"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* サービス2 */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=attractive%20landing%20page%20design%20on%20laptop%20screen%2C%20conversion%20focused%20layout%20with%20call%20to%20action%20buttons%2C%20pastel%20lavender%20and%20pink%20gradient%20background%2C%20modern%20web%20design%2C%20professional%20marketing%20page%20mockup&width=600&height=450&seq=service-detail-2&orientation=landscape"
                    alt="LP制作"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="bg-[#F5F0FF] inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm font-semibold text-[#E0BBE4]">02. LP制作</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
                  売れるLPで<br />お客様を増やす
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  商品やサービスの魅力を最大限に伝えるランディングページを制作。心理学に基づいた構成で、訪問者を自然にお問い合わせへ導きます。A/Bテストや改善提案も対応可能です 📈
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {['コンバージョン重視の設計', 'キャッチコピーも一緒に考案', 'スマホでも見やすいデザイン', '効果測定ツールの設置'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#E0BBE4] rounded-full text-white text-xs sm:text-sm flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* サービス3 */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-[#F0FFF4] inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm font-semibold text-[#B4E7CE]">03. チャット更新サポート</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
                  更新は<br />チャット1つで完結
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  「この写真を変えたい」「新しいメニューを追加したい」そんなときは、チャットで送るだけ。専門知識は一切不要。私たちが代わりに更新作業を行います。営業時間内なら、最短30分で対応 💬
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {['テキスト・画像の差し替え', '新規ページの追加', 'デザインの微調整', '緊急時の即日対応'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#B4E7CE] rounded-full text-white text-xs sm:text-sm flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=person%20updating%20website%20content%20on%20laptop%20computer%20screen%2C%20easy%20website%20editing%20interface%2C%20pastel%20mint%20green%20soft%20background%2C%20friendly%20workspace%20atmosphere%2C%20modern%20web%20content%20management%2C%20simple%20drag%20and%20drop%20editing%2C%20clean%20professional%20design&width=600&height=450&seq=service-detail-3-pc-update&orientation=landscape"
                    alt="チャット更新"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* サービス4 */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=ongoing%20website%20maintenance%20and%20support%20concept%2C%20computer%20screen%20showing%20analytics%20and%20updates%2C%20pastel%20color%20palette%2C%20professional%20web%20management%2C%20continuous%20improvement%2C%20friendly%20support%20team%20atmosphere&width=600&height=450&seq=service-detail-4&orientation=landscape"
                    alt="運用サポート"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="bg-[#FFF0F0] inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm font-semibold text-[#FF9B9B]">04. 運用サポート</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
                  公開後も<br />ずっと安心サポート
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  Webサイトは公開してからが本番。アクセス解析、改善提案、トラブル対応まで、長期的にサポートします。「こんなこと聞いていいのかな？」という小さな疑問も、いつでも気軽にご相談ください 🌱
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {['定期的なアクセス解析レポート', '改善提案とアドバイス', 'トラブル時の即時対応', 'SEO対策の継続サポート'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#FF9B9B] rounded-full text-white text-xs sm:text-sm flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* お客様の声セクション */}
      <section id="voice" className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#F8F8FF] to-[#FFF5F5]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm">
              <span className="text-xs sm:text-sm text-gray-700">💬 お客様の声</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              実際に使っている方の<br />リアルな感想です
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* 口コミ1 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#FF9B9B] to-[#FFB6B6] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  田
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">田中様</p>
                  <p className="text-xs sm:text-sm text-gray-500">カフェ経営</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#FF9B9B] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                チャットで『メニュー変更したい』と送ったら、30分で対応してくれました! 本当に助かります😊
              </p>
            </div>

            {/* 口コミ2 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#E0BBE4] to-[#E8C9E8] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  佐
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">佐藤様</p>
                  <p className="text-xs sm:text-sm text-gray-500">整体院</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#E0BBE4] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Web知識ゼロでしたが、全部おまかせできて安心。今では予約が増えて嬉しいです✨
              </p>
            </div>

            {/* 口コミ3 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#B4E7CE] to-[#C4F0DC] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  鈴
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">鈴木様</p>
                  <p className="text-xs sm:text-sm text-gray-500">ネイルサロン経営</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#B4E7CE] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                デザインがかわいくて、お客様からも好評です。更新も簡単で本当に楽になりました🌸
              </p>
            </div>

            {/* 口コミ4 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#FFD3B6] to-[#FFE0C7] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  高
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">高橋様</p>
                  <p className="text-xs sm:text-sm text-gray-500">ヨガ教室</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#FFD3B6] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                公開後のサポートがしっかりしていて安心。困ったときにすぐ相談できるのが嬉しいです🧘‍♀️
              </p>
            </div>

            {/* 口コミ5 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#FF9B9B] to-[#FFB6B6] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  山
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">山田様</p>
                  <p className="text-xs sm:text-sm text-gray-500">美容院</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#FF9B9B] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                お客様から『ホームページを見てきました』という声が増えて、集客効果を実感しています💇‍♀️
              </p>
            </div>

            {/* 口コミ6 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#E0BBE4] to-[#E8C9E8] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  伊
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm sm:text-base">伊藤様</p>
                  <p className="text-xs sm:text-sm text-gray-500">パン屋</p>
                </div>
              </div>
              <div className="text-4xl sm:text-5xl text-[#E0BBE4] mb-3">"</div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                料金も明確で安心。気軽に相談できるのがありがたいです🍞
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 料金プランセクション - 比較表の前に追加 */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#FFF5E6] via-[#F5F0FF] to-[#E8F5F1]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm">
              <span className="text-xs sm:text-sm text-gray-700">💰 料金プラン</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              わかりやすい<br className="md:hidden" />料金プラン
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              月額4,980円〜初期費用（サーバー・ドメイン・保守込み）
            </p>
          </div>

          {/* タブ切り替え */}
          <div className="flex justify-center mb-8 sm:mb-12 px-4">
            <div className="inline-flex bg-white rounded-full p-1 shadow-md w-full max-w-md sm:w-auto">
              <button
                onClick={() => setSelectedPlanType('lp')}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-xs sm:text-base ${
                  selectedPlanType === 'lp'
                    ? 'bg-[#FF9B9B] text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                LP制作
              </button>
              <button
                onClick={() => setSelectedPlanType('hp')}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-xs sm:text-base ${
                  selectedPlanType === 'hp'
                    ? 'bg-[#E0BBE4] text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                HP制作
              </button>
            </div>
          </div>

          {/* LPプラン */}
          {selectedPlanType === 'lp' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* ライトプラン */}
              <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2" style={{ color: '#42A5F5' }}>
                  ライト
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#42A5F5' }}>
                    68,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '3セクション',
                    'スマホ対応',
                    'メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#42A5F5' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#42A5F5' }}
                >
                  このプランを選ぶ
                </a>
              </div>

              {/* スタンダードプラン（人気） */}
              <div className="bg-gradient-to-br from-[#F3E5F5] to-[#E1BEE7] rounded-3xl p-6 sm:p-8 shadow-2xl hover:shadow-3xl transition-all relative sm:-mt-4 sm:mb-4">
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap text-white shadow-lg" style={{ backgroundColor: '#AB47BC' }}>
                  人気プラン ⭐
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2 mt-2" style={{ color: '#AB47BC' }}>
                  スタンダード
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#AB47BC' }}>
                    98,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '5セクション',
                    'スマホ対応',
                    '写真加工・補正サービス',
                    '高速表示',
                    'SEO基礎対策',
                    'メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#AB47BC' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700 font-semibold">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#AB47BC' }}
                >
                  このプランを選ぶ
                </a>
              </div>

              {/* プレミアムプラン */}
              <div className="bg-gradient-to-br from-[#E0F2F1] to-[#B2DFDB] rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all sm:col-span-2 lg:col-span-1">
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2" style={{ color: '#26A69A' }}>
                  プレミアム
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#26A69A' }}>
                    128,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '10セクション',
                    'スマホ対応',
                    '写真加工・補正サービス',
                    '高速表示',
                    'SEO対策',
                    '電話・メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#26A69A' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#26A69A' }}
                >
                  このプランを選ぶ
                </a>
              </div>
            </div>
          )}

          {/* HPプラン */}
          {selectedPlanType === 'hp' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* ライトプラン */}
              <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2" style={{ color: '#42A5F5' }}>
                  ライト
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#42A5F5' }}>
                    98,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '5ページ',
                    'スマホ対応',
                    'メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#42A5F5' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#42A5F5' }}
                >
                  このプランを選ぶ
                </a>
              </div>

              {/* スタンダードプラン（人気） */}
              <div className="bg-gradient-to-br from-[#F3E5F5] to-[#E1BEE7] rounded-3xl p-6 sm:p-8 shadow-2xl hover:shadow-3xl transition-all relative sm:-mt-4 sm:mb-4">
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap text-white shadow-lg" style={{ backgroundColor: '#AB47BC' }}>
                  人気プラン ⭐
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2 mt-2" style={{ color: '#AB47BC' }}>
                  スタンダード
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#AB47BC' }}>
                    148,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '10ページ',
                    'スマホ対応',
                    '写真加工・補正サービス',
                    '高速表示',
                    'SEO基礎対策',
                    'メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#AB47BC' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700 font-semibold">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#AB47BC' }}
                >
                  このプランを選ぶ
                </a>
              </div>

              {/* プレミアムプラン */}
              <div className="bg-gradient-to-br from-[#E0F2F1] to-[#B2DFDB] rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all sm:col-span-2 lg:col-span-1">
                <h3 className="text-2xl sm:text-3xl font-bold text-center mb-2" style={{ color: '#26A69A' }}>
                  プレミアム
                </h3>
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: '#26A69A' }}>
                    198,000円
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">初期費用</p>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {[
                    '15ページ',
                    'スマホ対応',
                    '写真加工・補正サービス',
                    '高速表示',
                    'SEO対策',
                    '電話・メールサポート',
                    'チャット更新',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-white text-xs sm:text-sm flex-shrink-0" style={{ backgroundColor: '#26A69A' }}>
                        ✓
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="block w-full text-center py-3 sm:py-4 rounded-full font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer text-white text-sm sm:text-base"
                  style={{ backgroundColor: '#26A69A' }}
                >
                  このプランを選ぶ
                </a>
              </div>
            </div>
          )}

          {/* 注意書き */}
          <div className="mt-8 sm:mt-12 text-center px-4">
            <p className="text-xs sm:text-sm text-gray-600 mb-2">
              ※ 月額4,980円（サーバー・ドメイン・保守込み）が別途かかります
            </p>
            <p className="text-xs sm:text-sm text-gray-600">
              ※ 公開までの更新作業は何度でも無料対応いたします 💬
            </p>
          </div>
        </div>
      </section>

      {/* 比較表セクション */}
      <section id="comparison" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-[#FFF0F0] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <span className="text-xs sm:text-sm text-gray-700">📊 他社との違い</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              つくーるうぇぶが<br className="md:hidden" />選ばれる理由
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 items-start">
            {/* 一般的な制作会社 */}
            <div className="bg-[#F8F8F8] rounded-2xl p-6 sm:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-gray-600 mb-4 sm:mb-6 text-center">一般的な制作会社</h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  { icon: '❌', text: '更新は1週間前後' },
                  { icon: '❌', text: '専門用語が多い' },
                  { icon: '❌', text: '納期が長い' },
                  { icon: '❌', text: 'サポートは期間限定' },
                  { icon: '❌', text: '連絡が取りづらい' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 sm:gap-3">
                    <span className="text-lg sm:text-xl flex-shrink-0">{item.icon}</span>
                    <span className="text-sm sm:text-base text-gray-600 leading-relaxed pt-1">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* つくーるうぇぶ（推奨） */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl relative md:-mt-4 md:mb-4">
              <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 bg-[#FF6B6B] text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                おすすめ ⭐
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center mt-2">つくーるうぇぶ</h3>
              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  { icon: '✅', text: 'チャット更新対応', bg: 'bg-[#FFE5E5]' },
                  { icon: '✅', text: 'やさしい言葉で説明', bg: 'bg-[#FFE5E5]' },
                  { icon: '✅', text: 'スピード納品', bg: 'bg-[#FFE5E5]' },
                  { icon: '✅', text: 'ずっとサポート', bg: 'bg-[#FFE5E5]' },
                  { icon: '✅', text: 'チャットで即レス', bg: 'bg-[#FFE5E5]' },
                ].map((item, i) => (
                  <li key={i} className={`flex items-start gap-2 sm:gap-3 ${item.bg} rounded-lg p-2 sm:p-3`}>
                    <span className="text-lg sm:text-xl flex-shrink-0">{item.icon}</span>
                    <span className="text-sm sm:text-base text-gray-800 font-semibold pt-1">{item.text}</span>
                  </li>
                ))}
              </ul>
              <a 
                href="#contact"
                className="block w-full bg-[#FF6B6B] text-white text-center py-3 sm:py-4 rounded-full font-bold hover:bg-[#FF5555] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                無料で相談する 💬
              </a>
            </div>

            {/* フリーランス */}
            <div className="bg-[#F8F8F8] rounded-2xl p-6 sm:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-gray-600 mb-4 sm:mb-6 text-center">フリーランス</h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  { icon: '△', text: '更新対応はまちまち' },
                  { icon: '△', text: '品質にばらつき' },
                  { icon: '△', text: '連絡が取りづらいことも' },
                  { icon: '△', text: 'サポート体制が不明確' },
                  { icon: '△', text: '急な対応が難しい' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 sm:gap-3">
                    <span className="text-lg sm:text-xl flex-shrink-0">{item.icon}</span>
                    <span className="text-sm sm:text-base text-gray-600 pt-1">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 実績紹介セクション */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#FFF5F5] to-[#F0FFF4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm">
              <span className="text-xs sm:text-sm text-gray-700">🎨 制作実績</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              これまでの<br className="md:hidden" />制作事例
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: 'カフェ様 HP',
                category: 'ホームページ',
                desc: 'やわらかい雰囲気のカフェサイト。メニュー更新もチャットで簡単に',
                color: 'from-[#FFE5E5] to-[#FFF0F0]',
                imageQuery: 'cozy cafe website homepage design with pastel pink and cream colors, warm welcoming atmosphere, menu section visible, coffee shop branding, soft minimalist aesthetic, multiple pages navigation, friendly business website layout, simple background',
              },
              {
                title: '整体院様 LP',
                category: 'ランディングページ',
                desc: '予約数が3倍に増加。信頼感を重視したデザイン',
                color: 'from-[#F5F0FF] to-[#FAF5FF]',
                imageQuery: 'professional chiropractic clinic landing page design with single page layout, pastel lavender color scheme, prominent call to action button, appointment booking section, trust building elements, conversion focused design, clean professional aesthetic, simple background',
              },
              {
                title: 'ネイルサロン様 HP',
                category: 'ホームページ',
                desc: 'かわいいデザインでお客様から好評。SNS連携も実装',
                color: 'from-[#F0FFF4] to-[#F5FFF8]',
                imageQuery: 'cute nail salon website homepage with pastel mint green and pink accents, gallery of nail art photos, social media integration icons, feminine elegant design, multiple pages structure, beauty salon branding, soft aesthetic, simple background',
              },
            ].map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all mb-4">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src={`https://readdy.ai/api/search-image?query=$%7Bitem.imageQuery%7D&width=500&height=375&seq=portfolio-${index + 1}&orientation=landscape`}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-20`}></div>
                  </div>
                </div>
                <div className="px-2">
                  <div className="inline-block bg-white px-3 py-1 rounded-full text-xs font-semibold text-gray-600 mb-2">
                    {item.category}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQセクション */}
      <section id="faq" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-[#F5F0FF] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <span className="text-xs sm:text-sm text-gray-700">❓ よくある質問</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              気になること、<br className="md:hidden" />ぜんぶお答えします
            </h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {[
              {
                q: 'Web知識がなくても大丈夫ですか？',
                a: 'もちろん大丈夫です! つくーるうぇぶは、Web知識ゼロの方でも安心してご利用いただけるよう、専門用語を使わず、やさしい言葉で丁寧にご説明します。わからないことがあれば、何度でもお気軽にご質問ください 😊',
              },
              {
                q: '更新はどのくらいの頻度でお願いできますか？',
                a: '更新回数に制限はありません。チャットで「この写真を変えたい」「新しいメニューを追加したい」と送っていただくだけで、何度でも対応いたします。小さな修正から大きな変更まで、お気軽にご依頼ください。',
              },
              {
                q: '料金プランを教えてください',
                a: '初期制作費用と月額4,980円（サーバー・ドメイン・保守込み）のプランをご用意しています。更新作業は別途対応となりますが、チャットで気軽にご依頼いただけます。詳しい料金は無料相談時にご案内いたします。',
              },
              {
                q: '制作期間はどのくらいですか？',
                a: 'ホームページもLPも1週間以内で公開可能です。お急ぎの場合は特急対応も承りますので、ご相談ください。',
              },
              {
                q: 'チャットでの更新対応の範囲は？',
                a: 'テキストや画像の差し替え、新規ページの追加、デザインの微調整など、幅広く対応しています。',
              },
              {
                q: 'スマホ対応はしていますか？',
                a: 'はい、すべてのサイトがスマホ・タブレット・PCに完全対応しています。',
              },
              {
                q: '公開後のサポートはありますか？',
                a: '公開後も継続的にサポートいたします。',
              },
              {
                q: '他社で作ったサイトのリニューアルも可能ですか？',
                a: 'もちろん可能です! 既存サイトの問題点を分析し、より良いサイトへとリニューアルいたします。',
              },
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-start gap-3 sm:gap-4 p-4 sm:p-6 text-left hover:bg-[#FFF5F5] transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#FFE5E5] rounded-full text-[#FF6B6B] font-bold flex-shrink-0 text-sm sm:text-base">
                    Q
                  </div>
                  <span className="flex-1 text-base sm:text-lg font-bold text-gray-800 pt-1">{faq.q}</span>
                  <div className={`w-6 h-6 flex items-center justify-center text-gray-400 transition-transform flex-shrink-0 ${
                    openFaq === index ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-xl sm:text-2xl"></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0 border-t border-gray-100">
                    <div className="flex items-start gap-3 sm:gap-4 mt-4">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#F0FFF4] rounded-full text-[#B4E7CE] font-bold flex-shrink-0 text-sm sm:text-base">
                        A
                      </div>
                      <p className="flex-1 text-sm sm:text-base text-gray-600 leading-relaxed pt-1">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#FF9B9B] to-[#FFB6B6] rounded-[32px] sm:rounded-[40px] overflow-hidden shadow-2xl">
            {/* 背景装飾 */}
            <div className="absolute top-10 right-10 w-48 sm:w-64 h-48 sm:h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-56 sm:w-80 h-56 sm:h-80 bg-white/10 rounded-full blur-3xl"></div>

            <div className="relative px-6 py-12 sm:px-8 sm:py-16 md:px-16 md:py-20 text-center">
              <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-6 sm:mb-8">
                <span className="text-xs sm:text-sm text-gray-700">✨ 今すぐ始めよう</span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
                Webの悩み、今日で終わりに
              </h2>
              <p className="text-lg sm:text-xl text-white/90 mb-3 sm:mb-4">
                無料相談は3分で完了。まずはお気軽にお話ししましょう
              </p>
              <p className="text-sm sm:text-base text-white/80 mb-8 sm:mb-10">
                あなたの事業に最適なWeb活用方法をご提案します 🌸
              </p>

              <a
                href="#contact-form"
                className="inline-flex items-center gap-2 bg-white text-[#FF9B9B] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                無料相談を申し込む 💬
              </a>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-white/70">
                <span>✓ 所要時間: 約3分</span>
                <span className="hidden sm:inline">|</span>
                <span>営業電話は一切ありません</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* お問い合わせフォームセクション */}
      <section id="contact-form" className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-white to-[#FFF5F5]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FF9B9B]/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm text-[#FF9B9B] font-medium">📝 お問い合わせ</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              無料相談フォーム
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              以下のフォームにご記入ください。24時間以内にご返信いたします 🌸
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 md:p-12">
            <form 
              data-readdy-form
              action="https://readdy.ai/api/form/d4q53b9mma3c958r839g"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                // バリデーション
                const name = formData.get('name') as string;
                const email = formData.get('email') as string;
                const phone = formData.get('phone') as string;
                const plan = formData.get('plan') as string;

                if (!name || !email || !phone || !plan) {
                  alert('すべての項目を入力してください');
                  return;
                }

                // メール形式チェック
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                  alert('正しいメールアドレスを入力してください');
                  return;
                }

                // 送信処理
                fetch('https://readdy.ai/api/form/d4q53b9mma3c958r839g', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                })
                  .then((response) => {
                    if (response.ok) {
                      alert('お問い合わせありがとうございます！24時間以内にご返信いたします。');
                      form.reset();
                    } else {
                      alert('送信に失敗しました。もう一度お試しください。');
                    }
                  })
                  .catch(() => {
                    alert('送信に失敗しました。もう一度お試しください。');
                  });
              }}
            >
              <div className="space-y-5 sm:space-y-6">
                {/* お名前 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-[#FF9B9B]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF9B9B] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* メールアドレス */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-[#FF9B9B]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF9B9B] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                {/* 電話番号 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号 <span className="text-[#FF9B9B]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF9B9B] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* プラン選択 */}
                <div>
                  <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-2">
                    ご希望のプラン <span className="text-[#FF9B9B]">*</span>
                  </label>
                  <select
                    id="plan"
                    name="plan"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF9B9B] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">プランを選択してください</option>
                    <optgroup label="ランディングページ制作">
                      <option value="LP-ライト">LP ライト - 68,000円（3セクション）</option>
                      <option value="LP-スタンダード">LP スタンダード - 98,000円（5セクション）⭐人気</option>
                      <option value="LP-プレミアム">LP プレミアム - 128,000円（10セクション）</option>
                    </optgroup>
                    <optgroup label="ホームページ制作">
                      <option value="HP-ライト">HP ライト - 98,000円（5ページ）</option>
                      <option value="HP-スタンダード">HP スタンダード - 148,000円（10ページ）⭐人気</option>
                      <option value="HP-プレミアム">HP プレミアム - 198,000円（15ページ）</option>
                    </optgroup>
                    <option value="その他">その他・相談したい</option>
                  </select>
                </div>

                {/* お問い合わせ内容（任意） */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    お問い合わせ内容（任意）
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF9B9B] focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="ご質問やご要望などがあればお書きください（500文字以内）"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">最大500文字まで</p>
                </div>

                {/* 送信ボタン */}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#FF9B9B] to-[#FFB6B6] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] whitespace-nowrap"
                >
                  無料相談を申し込む 🌸
                </button>

                <p className="text-xs text-gray-500 text-center">
                  ✓ 24時間以内にご返信いたします　✓ 営業電話は一切ありません
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-[#1A1F2E] text-white rounded-t-[24px] sm:rounded-t-[32px] mt-16 sm:mt-24 relative overflow-hidden">
        {/* 背景ウォーターマーク */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
          <p className="text-[80px] sm:text-[120px] font-bold text-[#252A38]">つくーるうぇぶ</p>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-20">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
            {/* ブランドエリア */}
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <img 
                  src="https://static.readdy.ai/image/ee58861ba7b6c31a9af012601f80f1d3/1349779ff3cc17dc8b22c137c4c0afcc.jpeg" 
                  alt="つくーるうぇぶ" 
                  className="h-8 sm:h-10 w-auto object-contain"
                />
                <h3 className="text-xl sm:text-2xl font-bold">つくーるうぇぶ</h3>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 sm:mb-8">
                Webのすべてをおまかせ。<br />
                あなたは事業に専念できます
              </p>
              <div className="flex gap-3">
                <a
                  href="https://www.instagram.com/tsuku_ru.web"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#2A2F3E] rounded-full hover:bg-[#FF9B9B] transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
                <a
                  href="mailto:tsukuru.web0482@gmail.com"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#2A2F3E] rounded-full hover:bg-[#FF9B9B] transition-colors cursor-pointer"
                >
                  <i className="ri-mail-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            {/* サービスリンク */}
            <div>
              <h4 className="text-base font-bold mb-4 sm:mb-6">サービス</h4>
              <ul className="space-y-2 sm:space-y-3">
                {[
                  { text: 'ホームページ制作', href: '#service' },
                  { text: 'LP制作', href: '#service' },
                  { text: '運用サポート', href: '#service' },
                  { text: '料金プラン', href: '#pricing' },
                  { text: '制作実績', href: '#' }
                ].map((item, i) => (
                  <li key={i}>
                    <a href={item.href} className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* サポート */}
            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="text-base font-bold mb-4 sm:mb-6">サポート</h4>
              <ul className="space-y-2 sm:space-y-3">
                {[
                  { text: 'よくある質問', href: '#faq' },
                  { text: 'お問い合わせ', href: '#contact' }
                ].map((item, i) => (
                  <li key={i}>
                    <a href={item.href} className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* コピーライト */}
          <div className="border-t border-gray-700 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-xs sm:text-sm text-gray-400 text-center md:text-left">
                © 2025 つくーるうぇぶ. All rights reserved.
              </p>
              <a 
                href="https://tuku-ruweb.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                つくーるうぇぶ
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
